import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, ArrowRight, ChevronDown, Sparkles, Check } from "lucide-react";
import { authApi, useAuth } from "../authApi";
import { PROFILE_TYPES } from "../profiles";

const inp = "w-full bg-white/[0.04] border border-white/12 px-4 py-2 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#C19A6B] focus:bg-white/[0.06] transition-colors rounded-[5px]";
const lbl = "block text-[10px] uppercase tracking-[0.16em] text-zinc-400 mb-1";
const TEAM_IMG = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/6de08a343b71cb8de9cccbb460230129fad2274eebb01bc780d0b398cfd66f20.png";

const JOIN_ADVANTAGES = [
  "Un réseau national d'agents et de conseillers de confiance.",
  "Des outils IA pour valoriser vos biens et gagner du temps.",
  "Des opportunités off market partagées avant leur diffusion.",
];

export default function JoinModal({ open, onClose, referrerCode = "", referrerName = "", defaultType = "" }) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ account_type: "", first_name: "", last_name: "", email: "", password: "", also_buyer: null, apporteur_status: "", apporteur_pro_type: "", siret: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) { setErr(""); setF((p) => ({ ...p, account_type: defaultType || p.account_type })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType]);

  const isApporteur = f.account_type === "Apporteur d'affaires";
  const apporteurValid = !isApporteur || (f.apporteur_status === "particulier" || (f.apporteur_status === "professionnel" && f.apporteur_pro_type && (f.siret || "").replace(/\s/g, "").length >= 9));
  const valid = f.account_type && f.first_name && f.last_name && f.email && f.password.length >= 6 && apporteurValid;

  const submit = async () => {
    if (!valid) { setErr("Complétez tous les champs (mot de passe : 6 caractères minimum)."); return; }
    setErr(""); setLoading(true);
    try {
      const r = await authApi.register({ ...f, also_buyer: f.also_buyer === true, network_mode: "national", ref: referrerCode || "" });
      setUser(r.user);
      nav("/mon-compte");
    } catch (e) { setErr(e.message || "Inscription impossible."); setLoading(false); }
  };

  const google = () => {
    try {
      if (referrerCode) sessionStorage.setItem("om_ref", referrerCode);
      if (f.account_type) sessionStorage.setItem("om_type", f.account_type);
    } catch (_) {}
    const redirectUrl = window.location.origin + "/mon-compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose} data-testid="join-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-[5px] border border-white/15 bg-[#0d0d10] shadow-2xl text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div aria-hidden="true" className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />
            <button onClick={onClose} data-testid="join-modal-close" className="absolute top-4 right-4 z-10 text-zinc-300 hover:text-white transition-colors"><X className="w-5 h-5" /></button>

            <div className="grid md:grid-cols-2">
              {/* Colonne branding — photo équipe (desktop) */}
              <div className="hidden md:flex flex-col justify-end gap-3 p-6 pb-16 relative overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url(${TEAM_IMG})` }}>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent" />
                <div className="absolute inset-y-0 right-0 w-28 bg-gradient-to-r from-transparent to-[#0d0d10]" />
                <div className="relative z-10 flex flex-col gap-4">
                  <div>
                    <p className="calli text-3xl text-[#C19A6B] leading-none">Rejoignez le réseau</p>
                    <p className="text-sm text-zinc-200 mt-3 leading-relaxed">Un écosystème immobilier premium, augmenté par l'IA, où chaque professionnel garde son identité tout en profitant de la force d'un réseau national.</p>
                  </div>
                  <ul className="space-y-2.5 mt-1">
                    {JOIN_ADVANTAGES.map((a) => (
                      <li key={a} className="flex items-start gap-2.5 text-sm text-zinc-100">
                        <Check className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" /> <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                  {referrerName && <p className="text-xs text-zinc-300 pt-1">Sur recommandation de <span className="text-[#C19A6B]">{referrerName}</span>.</p>}
                </div>
              </div>

              {/* Colonne formulaire */}
              <div className="p-5 space-y-2.5">
                {/* En-tête mobile */}
                <div className="md:hidden pr-8">
                  <p className="calli text-2xl text-[#C19A6B] leading-none">Rejoignez le réseau</p>
                  <p className="text-xs text-zinc-400 mt-1.5">Créez votre compte professionnel — validé par l'équipe OFF MARKETTE.</p>
                </div>
                <h3 className="hidden md:block text-xl font-light text-white tracking-tight">Créer mon compte professionnel</h3>

                <div>
                  <label className={lbl}>Votre profil</label>
                  <div className="relative">
                    <select value={f.account_type} onChange={(e) => set("account_type", e.target.value)} className={`${inp} appearance-none pr-11 cursor-pointer`} data-testid="join-type">
                      <option value="" className="bg-[#0d0d10]">— Sélectionner votre profil —</option>
                      {PROFILE_TYPES.map((t) => <option key={t} value={t} className="bg-[#0d0d10]">{t}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-[#C19A6B] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                {isApporteur && (
                  <div className="rounded-[5px] border border-[#C19A6B]/25 bg-[#C19A6B]/[0.06] p-3.5 space-y-3" data-testid="join-apporteur-status">
                    <label className={lbl}>Vous êtes apporteur en tant que</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[["particulier", "Particulier", "Occasionnel · plafond 2 500 €/an"], ["professionnel", "Professionnel", "Auto-entrepreneur ou société"]].map(([val, label, hint]) => (
                        <button key={val} type="button" data-testid={`join-apporteur-${val}`}
                          onClick={() => set("apporteur_status", val)}
                          className={`text-left p-3 rounded-[5px] border transition-colors ${f.apporteur_status === val ? "border-[#C19A6B] bg-[#C19A6B]/12 text-white" : "border-white/12 text-zinc-400 hover:border-white/30"}`}>
                          <span className="block text-sm font-medium">{label}</span>
                          <span className="block text-[10px] text-zinc-500 mt-0.5 leading-snug">{hint}</span>
                        </button>
                      ))}
                    </div>
                    {f.apporteur_status === "professionnel" && (
                      <div className="space-y-3 pt-1" data-testid="join-apporteur-pro">
                        <div>
                          <label className={lbl}>Forme juridique</label>
                          <div className="grid grid-cols-2 gap-3">
                            {[["auto-entrepreneur", "Auto-entrepreneur"], ["societe", "Société"]].map(([val, label]) => (
                              <button key={val} type="button" data-testid={`join-protype-${val}`}
                                onClick={() => set("apporteur_pro_type", val)}
                                className={`py-2 text-xs rounded-[5px] border transition-colors ${f.apporteur_pro_type === val ? "border-[#C19A6B] bg-[#C19A6B]/12 text-white" : "border-white/12 text-zinc-400 hover:border-white/30"}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className={lbl}>Numéro SIRET</label>
                          <input className={inp} placeholder="123 456 789 00012" value={f.siret} onChange={(e) => set("siret", e.target.value)} data-testid="join-siret" />
                        </div>
                      </div>
                    )}
                    {f.apporteur_status === "particulier" && (
                      <p className="text-[10px] text-zinc-400 leading-relaxed">Vos revenus d'apport sont suivis automatiquement. Au-delà de 2 000 €, nous vous proposerons de passer en statut professionnel.</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>Prénom</label>
                    <input className={inp} placeholder="Jean" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="join-firstname" />
                  </div>
                  <div>
                    <label className={lbl}>Nom</label>
                    <input className={inp} placeholder="Dupont" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="join-lastname" />
                  </div>
                </div>
                <div>
                  <label className={lbl}>E-mail professionnel</label>
                  <input className={inp} type="email" placeholder="jean@exemple.com" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="join-email" />
                </div>
                <div>
                  <label className={lbl}>Mot de passe (6 car. min.)</label>
                  <input className={inp} type="password" placeholder="••••••••" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="join-password" />
                </div>

                <div className="pt-1">
                  <label className={lbl}>Êtes-vous également acheteur ?</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[["Oui", true], ["Non", false]].map(([label, val]) => (
                      <button key={label} type="button" data-testid={`join-buyer-${val ? "yes" : "no"}`}
                        onClick={() => set("also_buyer", val)}
                        className={`py-2.5 text-sm rounded-[5px] border transition-colors ${f.also_buyer === val ? "border-[#C19A6B] bg-[#C19A6B]/12 text-white" : "border-white/12 text-zinc-400 hover:border-white/30"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {err && <p className="text-sm text-red-400" data-testid="join-error">{err}</p>}

                <button onClick={submit} disabled={loading} data-testid="join-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-black font-semibold uppercase tracking-wider text-sm py-2.5 rounded-[5px] transition-colors">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Créer mon compte <ArrowRight className="w-4 h-4" /></>}
                </button>

                <div className="flex items-center gap-3 py-0.5">
                  <div className="flex-1 h-px bg-white/10" /><span className="text-[10px] uppercase tracking-wider text-zinc-600">ou</span><div className="flex-1 h-px bg-white/10" />
                </div>
                <button onClick={google} data-testid="join-google" className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B] text-white text-sm py-2.5 rounded-[5px] transition-colors">
                  <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.3 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
                  Continuer avec Google
                </button>
                <p className="text-[11px] text-zinc-500 leading-relaxed pt-1">En créant un compte, votre profil est soumis à validation. Vous pourrez ensuite compléter votre présentation, vos annonces et votre site.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
